/**
 * Email service utility functions using EmailJS
 */
import emailjs from '@emailjs/browser';
import { emailJSConfig } from '../config/emailConfig';

// Initialize EmailJS with your public key
emailjs.init(emailJSConfig.publicKey);

console.log("PublickKey:", emailJSConfig.publicKey);

/**
 * Formats form data into a readable email template
 * @param {Object} formData - The loan application form data
 * @returns {Object} - Formatted template parameters for EmailJS
 */
const formatEmailTemplate = (formData) => {
  const currentDate = new Date().toLocaleDateString();
  const currentTime = new Date().toLocaleTimeString();
  
  // Format the form data into a readable structure
  const templateParams = {
    // Basic Information
    to_email: emailJSConfig.adminEmail,
    submission_date: currentDate,
    submission_time: currentTime,
    
    // Applicant Information
    applicant_type: formData.applicantType || 'Not specified',
    full_name: formData.fullName || 'Not provided',
    email: formData.email || 'Not provided',
    phone_number: formData.phoneNumber || 'Not provided',
    address: formData.address || 'Not provided',
    
    // Employment/Business Information
    employment_category: formData.employmentCategory || 'Not applicable',
    business_name: formData.businessName || 'Not provided',
    business_nature: formData.businessNature || 'Not applicable',
    other_business_nature: formData.otherBusinessNature || 'Not applicable',
    years_in_operation: formData.yearsInOperation || 'Not provided',
    monthly_income: formData.monthlyIncome || 'Not provided',
    
    // Personal Information (for employed individuals)
    date_of_birth: formData.dateOfBirth || 'Not applicable',
    
    // Loan Information
    loan_amount: formData.loanAmount ? `₦${Number(formData.loanAmount).toLocaleString()}` : 'Not provided',
    loan_purpose: formData.loanPurpose || 'Not provided',
    other_purpose: formData.otherPurpose || 'Not applicable',
    repayment_duration: formData.repaymentDuration || 'Not provided',
    
    // Existing Loans Information
    existing_loans: formData.existingLoans || 'Not provided',
    servicing_loans: formData.servicing || 'Not applicable',
    total_borrowing: formData.totalBorrowing ? `₦${Number(formData.totalBorrowing).toLocaleString()}` : 'Not applicable',
    borrowing_date: formData.borrowingDate || 'Not applicable',
    current_exposure: formData.currentExposure ? `₦${Number(formData.currentExposure).toLocaleString()}` : 'Not applicable',
    lending_institutions: formData.lendingInstitutions || 'Not applicable',
    bvn: formData.bvn || 'Not applicable',
    
    // Business Specific Information
    has_invoices: formData.hasInvoices || 'Not applicable',
    
    // Contact Preferences
    preferred_contact_method: formData.preferredContactMethod || 'Not provided',
    consent: formData.consent ? 'Yes' : 'No',
    
    // Raw form data as JSON (for backup/debugging)
    raw_form_data: JSON.stringify(formData, null, 2)
  };
  
  return templateParams;
};

/**
 * Sends loan application data via EmailJS
 * @param {Object} formData - The loan application form data
 * @returns {Promise} - A promise that resolves with the submission result
 */
export const submitLoanApplicationViaEmail = async (formData) => {
  try {
    // Validate required EmailJS configuration
    if (!emailJSConfig.serviceId || !emailJSConfig.templateId || !emailJSConfig.publicKey) {
      throw new Error('EmailJS configuration is missing. Please check your service ID, template ID, and public key.');
    }
    
    // Format the form data for the email template
    const templateParams = formatEmailTemplate(formData);

    // console.log('Email sent successfully:', templateParams);
    
    // Send email using EmailJS
    const result = await emailjs.send(
      emailJSConfig.serviceId,
      emailJSConfig.templateId,
      templateParams
    );
    
    console.log('Email sent successfully:', result);
    
    return {
      success: true,
      message: "Loan application submitted successfully via email",
      emailResult: result
    };
    
  } catch (error) {
    console.error("Error submitting loan application via EmailJS:", error);
    
    // Return a more specific error message
    let errorMessage = "Failed to submit loan application. Please try again.";
    
    if (error.text) {
      errorMessage = `Email service error: ${error.text}`;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    throw new Error(errorMessage);
  }
};
